import axios from 'axios';
import React, { useEffect, useState, useCallback } from "react";
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Link, useNavigate } from "react-router-dom";
import Input from "../../components/Input";
import { ErrorToast, SuccessToast } from "../../components/toastMessage";
import { BASE_URL, RECAPTCHA_SITE_KEY } from "../../constants/Constants";
import { useSetUser } from "../../contexts/UserContext";
import { useSetUserRegistration } from "../../contexts/UserRegistrationContext";
import { useDebounce } from "../../utils/common";
import { cookieUtils } from "../../utils/cookieUtils";
import style from "../login/login.module.css";
import { _axios } from '../../utils/axios';
//utils 
import { fetchAndVerifyUserDetails } from '../../libs/jwtUtils';

const Login = () => {
  const setUser = useSetUser();
  const setUserRegistration = useSetUserRegistration();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const navigate = useNavigate();
  const [existingLogo, setExistingLogo] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);


  useEffect(() => {
    const fetchImages = async () => {
      try {
        // Fetch logo
        const logoResponse = await _axios.get(`${BASE_URL}/api/logo`);
        if (logoResponse.data.success === 1) {
          setLogoUrl(logoResponse.data.url);
          if (logoResponse.data.filename) {
            setExistingLogo(logoResponse.data.filename);
          }
        }
      } catch (error) {
        console.error('Error fetching images:', error);
      }
    };

    fetchImages();
  }, []);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleVerify = (token: React.SetStateAction<string>) => {
    setToken(token);
    console.log("recaptcha V3", token);
  };

  const logLoginDetails = async (user: {
    name: any;
    role: any;
    email: any;
    mobile: any;
  }) => {
    try {
      const response = await _axios.post(`${BASE_URL}/api/users/log-login`, {
        name: user.name,
        role: user.role,
        email: user.email,
        mobile: user.mobile,
      });
      console.log("Login details logged successfully", response);
    } catch (error) {
      console.error("Error logging login details", error);
    }
  };

  const hashPassword = (password: string) => {
    const hashedPassword = btoa(password);
    return hashedPassword;
  };

  const clearAllCookies = () => {
    // Clear all known cookies
    cookieUtils.removeSecureCookie('userDetails');
    cookieUtils.removeSecureCookie('token');
    cookieUtils.removeSecureCookie('subRole');
    cookieUtils.removeSecureCookie('userRegistrationDetails');
  };

  // Helper function with verification loop
  const ensureCookiesWrittenAndVerified = async (expectedData: any, maxRetries = 5) => {
    for (let i = 0; i < maxRetries; i++) {
      // Wait
      await new Promise(resolve => setTimeout(resolve, 300));

      // Verify cookie was written
      const storedCookie = cookieUtils.getSecureCookie("userRegistrationDetails");

      if (storedCookie && storedCookie.regestration_status === expectedData.regestration_status) {
        console.log(`✅ Cookie verified on attempt ${i + 1}`);
        return true;
      }

      console.log(`⏳ Waiting for cookie... attempt ${i + 1}/${maxRetries}`);
    }

    console.warn('⚠️ Cookie verification timeout');
    return false;
  };

  // Use useCallback to memoize the function and prevent closure issues
  const onLogin = useCallback(async () => {
    // Prevent multiple simultaneous login attempts
    if (isLoading) {
      console.log('Login already in progress...');
      return;
    }

    setIsLoading(true);

    try {
      localStorage.clear();
      clearAllCookies();
      console.log('=== LOGIN START ===');

      if (email.length === 0 || password.length === 0) {
        ErrorToast("Please enter valid email/number or password");
        setIsLoading(false);
        return;
      }
      if (!isNaN(Number(email)) && email.length < 10) {
        ErrorToast("Please enter valid mobile number");
        setIsLoading(false);
        return;
      }
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

      const isValid = emailRegex.test(email);
      if (isNaN(Number(email)) && !isValid) {
        ErrorToast("Please enter valid email");
        setIsLoading(false);
        return;
      }

      const encodedPassword = hashPassword(password);
      const loginData = !isNaN(Number(email))
        ? { "mobile": email, "password": encodedPassword, "token": token }
        : { "email": email, "password": encodedPassword, "token": token };

      console.log('Sending login request...');

      const response = await _axios.post(`${BASE_URL}/api/users/login`, loginData);

      console.log('Login response received');

      if (response?.data?.success === 0) {
        ErrorToast(response?.data?.message);
        setIsLoading(false);
        return;
      }

      if (response?.data?.data?.active === 0) {
        ErrorToast(
          "Your account is currently inactive. Please contact your system administrator to reactivate the account."
        );
        setIsLoading(false);
        return;
      }

      const data = response?.data?.data;

      console.log('=== STORING BASIC USER DATA ===');
      // Store basic user data
      cookieUtils.setSecureCookie('userDetails', data, { expires: 1 });
      cookieUtils.setSecureCookie('token', response?.data?.token, { expires: 1 });
      if (data.subRole) {
        cookieUtils.setSecureCookie('subRole', data.subRole, { expires: 1 });
      }

      if (data?.name && data?.role) setUser(data);

      // Log login details
      await logLoginDetails(data);

      // Admin/Level/Accountant users - simple navigation
      if (data?.role === 'System Admin' || data?.role === 'Level 1' ||
        data?.role === 'Level 2' || data?.role === 'Level 3' || data?.role === 'Accountant') {

        if (data?.role === 'System Admin') {
          cookieUtils.setSecureCookie('isAdminMode', true, { expires: 1 });
        }

        SuccessToast('Login Successfully');
        console.log('Admin/Level user - redirecting...');

        // Simple delay and redirect
        await new Promise(resolve => setTimeout(resolve, 800));
        window.location.replace('/launch-page');
        return;
      }

      // Regular users (Architect, Engineer, Supervisor, Plot Owner)
      console.log('=== REGULAR USER LOGIN ===');
      SuccessToast("Login Successfully");

      // 1️⃣ Fetch user details
      console.log('Fetching user details...');
      let userDetailsResponse = await fetchAndVerifyUserDetails(data?.user_id);

      if (!userDetailsResponse?.data?.data || Object.keys(userDetailsResponse.data?.data).length === 0) {
        console.warn("No user details - redirecting to verification");
        await new Promise(resolve => setTimeout(resolve, 800));
        window.location.replace("/onboarding/verification-page");
        return;
      }

      if (!userDetailsResponse.success) {
        console.error("Token validation failed");
        alert("Something wrong with token");
        setIsLoading(false);
        return;
      }

      // 2️⃣ Extract and process user details
      let userDetails = userDetailsResponse.data?.data;

      console.log('User Type:', userDetails.user_type);
      console.log('Registration Status (from API):', userDetails.regestration_status);

      // 🔥 Force Plot Owner to approved status
      const isPlotOwner = userDetails.user_type === 'Plot Owner';
      if (isPlotOwner) {
        console.log('🔥 PLOT OWNER - Forcing approved status');
        userDetails = {
          ...userDetails,
          regestration_status: 1
        };
      }

      // 3️⃣ Store data in multiple places
      console.log('=== STORING USER REGISTRATION DETAILS ===');

      // Store in cookies - TRY MULTIPLE TIMES if needed
      for (let attempt = 0; attempt < 3; attempt++) {
        cookieUtils.setSecureCookie("userRegistrationDetails", userDetails, { expires: 7 });

        // Immediately verify it was stored
        const verification = cookieUtils.getSecureCookie("userRegistrationDetails");
        if (verification && verification.regestration_status === userDetails.regestration_status) {
          console.log(`✅ Cookie stored successfully on attempt ${attempt + 1}`);
          break;
        } else {
          console.log(`⚠️ Cookie storage attempt ${attempt + 1} failed, retrying...`);
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      // Store in localStorage
      localStorage.setItem("userRegistrationDetails", JSON.stringify(userDetails));

      // Update React state
      setUserRegistration(userDetails);

      // 4️⃣ Wait and verify storage worked
      console.log('Verifying storage...');
      const verified = await ensureCookiesWrittenAndVerified(userDetails, 5);

      if (!verified) {
        console.warn('⚠️ Could not verify cookie storage, but proceeding anyway');
      }

      // 5️⃣ Final verification before navigation
      const finalCookie = cookieUtils.getSecureCookie("userRegistrationDetails");
      const finalLocal = localStorage.getItem("userRegistrationDetails");

      console.log('=== FINAL VERIFICATION ===');
      console.log('Cookie:', finalCookie ? `status=${finalCookie.regestration_status}` : 'MISSING');
      console.log('LocalStorage:', finalLocal ? 'EXISTS' : 'MISSING');

      // 6️⃣ Navigation decision
      const registrationStatus = userDetails.regestration_status;

      console.log('=== NAVIGATION DECISION ===');
      console.log('Registration Status:', registrationStatus);
      console.log('Is Plot Owner:', isPlotOwner);

      // One more delay before navigation
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (registrationStatus === 1) {
        console.log('✅ APPROVED - Redirecting to launch-page');
        window.location.replace("/launch-page");
      } else {
        console.log('⏳ PENDING - Redirecting to not-verified');
        window.location.replace("/user-account-not-verified");
      }

    } catch (error) {
      console.error('=== LOGIN ERROR ===', error);
      ErrorToast("Login failed. Please try again.");
      setIsLoading(false);
    }
  }, [email, password, token, isLoading, setUser, setUserRegistration]);

  // Handle login button click
  const handleLoginClick = () => {
    onLogin();
  };

  // Handle Enter key press
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !isLoading) {
        event.preventDefault();
        onLogin();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [onLogin, isLoading]);

  const isUserLogin = async () => {
    const userDetails = await localStorage.getItem("userDetails");
    if (userDetails !== null) {
      navigate("/launch-page");
    }
  };

  useEffect(() => {
    isUserLogin();
  }, []);

  return (
    // <GoogleReCaptchaProvider reCaptchaKey="6Ldy7j0sAAAAAK96npJL_p3ghiggm7ew8TN4V6U2">
    <div className={style.loginContainer}>
      <div className={style.loginForm}>
        <div className={style.logo}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo"
              className={style.companyIcon}
            />
          )}
        </div>
        <div className={style.loginText}>Login</div>
        <br />
        <Input
          title={"Email/Mobile Number"}
          placeholder={"Email/Mobile Number"}
          isIcon={true}
          type="text"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input
          title={"Password"}
          placeholder={"Password"}
          isIcon={true}
          icon={`fa ${showPassword ? "fa-eye" : "fa-eye-slash"}`}
          type={showPassword ? "text" : "password"}
          onClick={togglePasswordVisibility}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div className={style.forgotPassword}>
          <Link to="/onboarding/forgot-password" className={style.greyLink}>
            Forgot Password?
          </Link>
        </div>

        <button
          className={style.loginButton}
          onClick={handleLoginClick}
          disabled={isLoading}
        >
          {isLoading ? "Logging in..." : "Login"}
        </button>

        <div className={style.createAccount}>
          Don't have an account yet?{" "}
          <Link to="/onboarding/create-account" className={style.greyLink}>
            Create an account
          </Link>
        </div>
      </div>
    </div>
    // <GoogleReCaptcha onVerify={handleVerify} />
    // </GoogleReCaptchaProvider>
  );
};

export default Login;
